import { useCallback, useRef } from 'react';
import { useEvents } from '../use-events/useEvents';
import { useClient } from '@/hooks/ClientContext';
import { OBRIGATORY_DB_TABLES } from '@/constants';
import { useUserContext } from '@/hooks/userContext/UserContext';
import type { InconsistencyProblem } from '@/shared/inconsistenciesData';
import { useDbFunctions } from '../dbFunctions/useDBFunctions';
import { invalidateBfSellerCache } from '@/shared/bfSeller';
import { clearProductDictionary, invalidateProductIndex } from '../productCatalog';
import { normalizeDatasetType, updateProgress, getDatasetKey } from './_syncCommons';
import type { OnDatasetPersisted } from './_useFetchFiles';
import {
  getOnStorage,
  invalidateCatalogCachesAfterSync,
  isImageDataset,
  mergeDatasetsByUrl,
  migrateImageDatasetsFromMain,
  OBRIGATORY_EMPTY_BLOCK_REASON,
  removeFromStorage,
  writeImageDatasets,
} from '@/shared';
import { CustomerDataProps, DataSetsProps, Tables } from '@/types';

let globalSyncInProgress = false;

/**
 * Um arquivo já gravado no `BF_SELLER_DATASETS` pelo `_useFetchFiles`, do ponto de vista do relatório.
 * `store` é o nome canônico da tabela (o alias `preco_novo` vira `preco`, e assim por diante).
 */
type WriteReportEntry = { key: string; st: number; obs?: string };

const isObrigatoryDataset = (table: string): boolean => OBRIGATORY_DB_TABLES.includes(table as Tables);

type PrepareFilesProps = {
  isSuccess: boolean;
  preparing: boolean;
  setError: (error: boolean) => void;
  setIsLoaded: (isLoaded: boolean) => void;
  setNoUpdate: (noUpdate: boolean) => void;
  setPreparing: (preparing: boolean) => void;
  setIsSuccess: (isSuccess: boolean) => void;
  setDataSets: (dataSets: DataSetsProps[]) => void;
  setIsOpenedModal: (isOpenedModal: boolean) => void;
  fetchDatasets: (datasets: DataSetsProps[], onPersisted?: OnDatasetPersisted) => Promise<any>;
  setDownloadingFileName: (downloadingFileName: string) => void;
  setDownloadSpeedBps: (downloadSpeedBps: number) => void;
  setProgressPercent: (progressPercent: number) => void;
};

/**
 * Orquestra o sync: baixa os arquivos, confere o que foi gravado no `BF_SELLER_DATASETS` e reporta.
 *
 * A gravação em si acontece arquivo a arquivo dentro do `_useFetchFiles`, logo depois do parse —
 * é lá que o payload de um único arquivo existe isolado, com o escopo (loja/segmento) do
 * descritor. Aqui não se lê nada do IndexedDB nem se remonta dataset em memória: era esse ciclo
 * de ler-mesclar-reescrever que travava os aparelhos fracos.
 *
 * O único dataset que ainda é gravado por este hook é `imagens`, que continua no banco
 * `BF_SELLER_IMAGES` — já é chave-valor e ficou fora desta migração.
 */
export const usePrepareFiles = (_useState: PrepareFilesProps) => {
  const loading = useRef(false);
  const downloadLabelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const db = useDbFunctions();
  const { registerEvent } = useEvents(db);
  const { setCustomer } = useUserContext();
  const {
    setCampanhaProdutos,
    setCampanhaClientes,
    setCampanhaFaixa,
    setCampanhaTopMix,
    setIndicadorLoja,
    setIndicadoresProdutividade,
  } = useClient();

  const loadFiles = useCallback(
    async (newDatasets?: DataSetsProps[], skipGlobalGuard = false, isBackground = false) => {
      if (!skipGlobalGuard && globalSyncInProgress) return;
      if (!skipGlobalGuard) globalSyncInProgress = true;
      if (loading.current) return;

      loading.current = true;
      _useState.setError(false);
      _useState.setIsSuccess(false);
      updateProgress(0, 0, '.');
      try {
        const listToDownload = (newDatasets || []).map((d) => ({
          ...d,
          TIPO_ARQUIVO: normalizeDatasetType(d.TIPO_ARQUIVO),
        }));
        if (!listToDownload.length) {
          _useState.setNoUpdate(true);
          _useState.setIsSuccess(true);
          return true;
        }

        if (listToDownload.find((i) => i.TIPO_ARQUIVO === 'estoque')) {
          removeFromStorage('stock_left');
        }

        if (!isBackground) {
          _useState.setPreparing(false);
          _useState.setIsOpenedModal(true);
        }

        registerEvent({
          tipoEvento: 'PROCESSAMENTO',
          obsEvento: 'Iniciando processamento de dados',
        }).catch(() => {});

        _useState.setDataSets(listToDownload);
        _useState.setPreparing(true);

        const tSync = Date.now();
        const relatorio: WriteReportEntry[] = [];
        const blockingProblems: InconsistencyProblem[] = [];
        let hasError = false;
        /** Payloads pequenos que alimentam contextos do React no fim do run. */
        const paraContexto: Record<string, any> = {};

        /**
         * Fecha um arquivo: relatório, registro do download e o que vai para contexto.
         *
         * Roda logo depois de o arquivo ser gravado no IndexedDB e antes do próximo começar —
         * assim nada disso se acumula para uma varredura no fim do sync, e o modal reflete o
         * progresso real em vez de ficar parado em "Aguarde" depois do último byte.
         */
        const onDatasetPersisted: OnDatasetPersisted = async ({ dataset, type, write, data }) => {
          const table = String(write?.store ?? type);
          const escopo = write ? `loja ${write.scope.loja}/seg ${write.scope.segmento}` : 'escopo ?';

          if (data != null) paraContexto[type] = data;

          if (write?.ok) {
            const t = write.timing;
            const tempo = t ? ` em ${t.total}ms (put ${t.put})` : '';
            relatorio.push({ key: table, st: 1, obs: `${write.rows} linhas ${escopo}${tempo}` });
            return;
          }

          const motivo =
            write?.status === 'sem-linhas'
              ? 'payload sem linhas'
              : write?.status === 'sem-spec'
                ? 'dataset sem spec no BF_SELLER_DATASETS'
                : (write?.error ?? 'falha ao gravar no BF_SELLER_DATASETS');

          if (isObrigatoryDataset(table)) {
            const obs = `${OBRIGATORY_EMPTY_BLOCK_REASON} ${table} (${motivo}, ${escopo})`;
            relatorio.push({ key: table, st: 0, obs });
            blockingProblems.push({ key: table, obs });
            hasError = true;
            return;
          }

          relatorio.push({ key: table, st: 0, obs: `${motivo} ${escopo}` });
        };

        const resp = await _useState.fetchDatasets(listToDownload, onDatasetPersisted);

        // Imagens ficam numa chave de storage separada da lista principal de datasets.
        const oldImageDatasets = migrateImageDatasetsFromMain();
        const successfullyDownloaded = listToDownload.filter((d, index) => resp?.byDatasetKey?.[getDatasetKey(d, index)]);
        const successfullyDownloadedImages = successfullyDownloaded.filter((dataset) => isImageDataset(dataset));

        if (successfullyDownloadedImages.length) {
          writeImageDatasets(mergeDatasetsByUrl(oldImageDatasets, successfullyDownloadedImages));
        }

        // Havia o que baixar e nada chegou: o `setError` do fetch já disparou, mas o run não pode
        // terminar como sucesso só porque não há resultado de gravação para percorrer.
        if (!successfullyDownloaded.length) {
          const obs = 'nenhum arquivo baixado com sucesso';
          relatorio.push({ key: 'download', st: 0, obs });
          blockingProblems.push({ key: 'download', obs });
          hasError = true;
        }

        // Os arquivos mudaram: índice em memória, índice de produtos e dicionário de produtos
        // calculados viram lixo. O `invalidateCatalogCachesAfterSync` fecha a lista, descartando
        // os catálogos e preços que o `PricesContext` guarda por cliente — `deferMemoryUntilSafe`
        // porque um sync em background não pode esvaziar a vitrine no meio de um pedido.
        invalidateBfSellerCache();
        invalidateProductIndex();
        clearProductDictionary();
        invalidateCatalogCachesAfterSync({ deferMemoryUntilSafe: true });

        const msSync = Date.now() - tSync;
        console.log(`[sync] total ${msSync}ms`, relatorio);

        void registerEvent({
          tipoEvento: 'DB_FUNCTIONS',
          obsEvento: `Salvando dados (${msSync}ms): ${relatorio
            .map((item) => {
              const obs = item.obs ? `[${item.obs}]` : '';
              return `${item.st} ${item.key} ${obs}`;
            })
            .join('|')}`,
          stEvento: hasError ? 0 : 1,
        }).catch(() => {});

        if (downloadLabelTimeoutRef.current) {
          clearTimeout(downloadLabelTimeoutRef.current);
          downloadLabelTimeoutRef.current = null;
        }

        const rcaCustomer = getOnStorage('rca_usuario');
        setCustomer(rcaCustomer as CustomerDataProps);

        // Só sobrescreve o contexto com o que veio neste run — dataset não baixado não zera nada.
        const incoming = (type: string) => paraContexto[type];
        const campanhaProduto = incoming('campanha_produto');
        const campanhaCliente = incoming('campanha_cliente') ?? incoming('campanhaClientes');
        const campanhaFaixa = incoming('campanha_faixa');
        const campanhaTopMix = incoming('campanha_mix_fornecedor');
        const indicadorLoja = incoming('indicador_loja');
        const indicadores = incoming('indicadores');

        if (campanhaProduto != null) setCampanhaProdutos(campanhaProduto);
        if (campanhaCliente != null) setCampanhaClientes(campanhaCliente);
        if (campanhaFaixa != null) setCampanhaFaixa(campanhaFaixa);
        if (campanhaTopMix != null) setCampanhaTopMix(campanhaTopMix);
        if (indicadorLoja != null) setIndicadorLoja(indicadorLoja);
        if (indicadores != null) setIndicadoresProdutividade(indicadores);

        _useState.setPreparing(false);
        loading.current = false;

        if (hasError) {
          _useState.setError(true);
          _useState.setIsSuccess(false);
          console.warn('[RCA sync] Finalizado com erro ao gravar dados no BF_SELLER_DATASETS');
          return undefined;
        }

        _useState.setIsSuccess(true);

        return true;
      } finally {
        loading.current = false;
        if (!skipGlobalGuard) {
          globalSyncInProgress = false;
        }
      }
    },
    [
      _useState.fetchDatasets,
      _useState.setDownloadingFileName,
      setCustomer,
      setCampanhaFaixa,
      setIndicadorLoja,
      setCampanhaTopMix,
      setCampanhaProdutos,
      setCampanhaClientes,
      setIndicadoresProdutividade,
    ]
  );

  return { loadFiles };
};
